import { withFieldGroup } from '@massilia/features'
import { cn, Input, Label, RadioGroup, RadioGroupItem } from '@massilia/ui'

import type { CheckoutFormValues } from '../checkout-form.schema'

interface PaymentSectionProps {
  installmentAvailable: boolean
  minimumDeposit: number
  promoCodeDiscount: null | number
  totalPrice: number
}

type PaymentSectionValues = Pick<CheckoutFormValues, 'depositAmount' | 'isPayingInMultipleTimes'>

export const PaymentSection = withFieldGroup({
  defaultValues: {} as PaymentSectionValues,
  props: {} as PaymentSectionProps,
  render: function PaymentFields({
    group,
    installmentAvailable,
    minimumDeposit,
    promoCodeDiscount,
    totalPrice,
  }) {
    const effectiveTotal = Math.max(0, totalPrice - (promoCodeDiscount ?? 0))

    return (
      <div className="flex w-full flex-col gap-4">
        <div className="text-gray-light-900 font-semibold">Vous souhaitez payer en</div>
        <group.Subscribe selector={(state) => state.values.isPayingInMultipleTimes}>
          {(isPayingInMultipleTimes) => (
            <RadioGroup
              className="flex flex-col gap-0"
              onValueChange={(value) => {
                group.setFieldValue(
                  'isPayingInMultipleTimes',
                  value === 'is-paying-in-multiple-times'
                )
              }}
              value={
                isPayingInMultipleTimes
                  ? 'is-paying-in-multiple-times'
                  : 'is-not-paying-in-multiple-times'
              }
            >
              {/* Full payment option */}
              <div
                className={cn(
                  'border-gray-light-300 flex w-full flex-col border-t border-r border-l transition-all',
                  installmentAvailable ? 'rounded-t-2xl' : 'rounded-2xl border-b',
                  !isPayingInMultipleTimes && 'border-brand-600 bg-brand-50 border-b'
                )}
              >
                <div className="flex items-start gap-2 p-4">
                  <RadioGroupItem
                    className="mt-0.5"
                    id="is-not-paying-in-multiple-times"
                    value="is-not-paying-in-multiple-times"
                  />
                  <div className="flex-1">
                    <Label
                      className="flex-1 text-sm font-semibold"
                      htmlFor="is-not-paying-in-multiple-times"
                    >
                      En intégralité
                    </Label>
                  </div>
                </div>
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    !isPayingInMultipleTimes
                      ? 'max-h-20 opacity-100'
                      : 'pointer-events-none max-h-0 opacity-0'
                  )}
                >
                  <div className="flex w-full items-center justify-between px-4 pb-4">
                    <div className="text-gray-light-900 text-sm font-semibold">À régler</div>
                    <div className="text-gray-light-900 text-lg font-semibold">
                      {`${effectiveTotal} €`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Multiple payments option */}
              {installmentAvailable && (
                <div
                  className={cn(
                    'border-gray-light-300 rounded-b-2xl border-r border-b border-l transition-all',
                    isPayingInMultipleTimes && 'border-brand-600 bg-brand-50 border-t'
                  )}
                >
                  <div className="flex items-start gap-2 p-4">
                    <RadioGroupItem
                      className="mt-0.5"
                      id="is-paying-in-multiple-times"
                      value="is-paying-in-multiple-times"
                    />
                    <div className="flex-1">
                      <Label
                        className="flex-1 text-sm font-semibold"
                        htmlFor="is-paying-in-multiple-times"
                      >
                        En plusieurs fois
                      </Label>
                    </div>
                  </div>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      isPayingInMultipleTimes
                        ? 'max-h-20 opacity-100'
                        : 'pointer-events-none max-h-0 opacity-0'
                    )}
                  >
                    <div className="flex flex-col items-start gap-2 px-4">
                      <div className="text-gray-light-700 text-sm font-medium">
                        Indiquer le montant
                      </div>
                      <group.AppField name="depositAmount">
                        {(field) => (
                          <div className="border-gray-light-300 mb-4 flex items-center gap-2 rounded-lg border bg-white">
                            <div className="text-gray-light-700 flex shrink-0 bg-white px-3 text-sm font-medium">
                              Minimum {`${minimumDeposit} €`}
                            </div>
                            <div className="flex items-center">
                              <Input
                                aria-invalid={
                                  typeof field.state.value === 'number' &&
                                  field.state.value < minimumDeposit
                                }
                                className={cn(
                                  'rounded-none border-y-0 border-r-0 text-right',
                                  'aria-invalid:border'
                                )}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? 0 : Number(e.target.value)
                                  field.handleChange(value)
                                }}
                                placeholder="0"
                                type="number"
                                value={
                                  !field.state.value || field.state.value === 0
                                    ? ''
                                    : field.state.value
                                }
                              />
                              <div className="border-gray-light-300 text-gray-light-700 rounded-r-lg bg-white px-3 py-2 text-sm font-medium">
                                €
                              </div>
                            </div>
                          </div>
                        )}
                      </group.AppField>
                    </div>
                  </div>
                </div>
              )}
            </RadioGroup>
          )}
        </group.Subscribe>
      </div>
    )
  },
})
